'use client';

/**
 * @file PreAuthReturnBanner
 * @description Banner for returning visitors with previously imported data
 * Reminds them to complete signup before data expires
 */

import { useCallback } from 'react';
import { FileSpreadsheet, Clock, ArrowRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface PreAuthReturnBannerProps {
  instrumentCount: number;
  fileName: string;
  timeRemaining: string | null;
  onResume: () => void;
  onDismiss: () => void;
  className?: string;
}

export function PreAuthReturnBanner({
  instrumentCount,
  fileName,
  timeRemaining,
  onResume,
  onDismiss,
  className,
}: PreAuthReturnBannerProps) {
  const handleDismiss = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    onDismiss();
  }, [onDismiss]);

  return (
    <div
      className={cn(
        'w-full bg-gradient-to-r from-primary/10 to-primary/5 border-b border-primary/20',
        className
      )}
    >
      <div className="container mx-auto px-4 py-3">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/20">
              <FileSpreadsheet className="h-5 w-5 text-primary" />
            </div>
            <div className="text-sm">
              <p className="font-medium">
                Welcome back! You have {instrumentCount} instruments ready
              </p>
              <p className="text-muted-foreground flex items-center gap-1">
                <span>From {fileName}</span>
                {timeRemaining && (
                  <>
                    <span className="mx-1">•</span>
                    <Clock className="h-3 w-3" />
                    <span>Expires in {timeRemaining}</span>
                  </>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={onResume}
              className="gap-2"
            >
              Continue to sign up
              <ArrowRight className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleDismiss}
              className="h-8 w-8 text-muted-foreground hover:text-foreground"
              aria-label="Dismiss banner"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PreAuthReturnBanner;
